import { Box, Button, Grid, TextField } from "@mui/material";
import { useState } from "react";

import Select from "../../../components/common/Select";
import { RequiredAsterisk } from "../../../components/professional/RequiredAsterisk";

const IcaoProficiencyForm = ({ icao, errors, setErrors, onSave, onCancel, dropDownOptions }) => {
  const [formData, setFormData] = useState({
    language: icao?.language || "",
    other_language: icao?.other_language || "",
    icao_level: icao?.icao_level || "",
    issuing_authority: icao?.issuing_authority || "",
    other_issuing_authority: icao?.other_issuing_authority || "",
    assessment_date: icao?.assessment_date || "",
    certificate_expiry_date: icao?.certificate_expiry_date || "",
    notes: icao?.notes || "",
  });

  const isVisible = formData.language && formData.language !== "other";
  const isExpiryDateVisible = formData.icao_level === "4";

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = () => {
    let valid = true;
    const icaoInfo = formData;

    if (!icaoInfo.language?.trim()) {
      setErrors((prev) => ({
        ...prev,
        language: "Language is required",
      }));
      valid = false;
    } else if (icaoInfo.language === "other" && !icaoInfo.other_language?.trim()) {
      setErrors((prev) => ({
        ...prev,
        other_language: "Please specify language",
      }));
      valid = false;
    }

    if (icaoInfo.issuing_authority === "other" && !icaoInfo.other_issuing_authority?.trim()) {
      setErrors((prev) => ({
        ...prev,
        other_issuing_authority: "Please specify issuing authority",
      }));
      valid = false;
    }
    return valid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };
  return (
    <form onSubmit={handleSubmit} className="space-y-4 my-3 sentry-mask">
      <Box
        sx={{
          px: 2,
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Select
              error={!!errors.language}
              helperText={errors.language}
              label={
                <span>
                  Language <RequiredAsterisk />
                </span>
              }
              options={dropDownOptions.icaoRecognizedLanguageChoices}
              onChange={(val) => handleChange("language", val)}
              value={formData.language}
            />
          </Grid>
          {formData.language === "other" && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                id="other_language"
                fullWidth
                variant="outlined"
                value={formData.other_language || ""}
                onChange={(e) => handleChange("other_language", e.target.value)}
                error={!!errors.other_language}
                helperText={errors.other_language}
                label={
                  <span>
                    Specify Language <RequiredAsterisk />
                  </span>
                }
              />
            </Grid>
          )}
          {isVisible && (
            <>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Select
                  error={!!errors.icao_level}
                  helperText={errors.icao_level}
                  label={<span>Icao level</span>}
                  options={dropDownOptions.icaoLevelChoices}
                  onChange={(val) => handleChange("icao_level", val)}
                  value={formData.icao_level}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Select
                  error={!!errors.issuing_authority}
                  helperText={errors.issuing_authority}
                  label={<span>Issuing Authority</span>}
                  options={dropDownOptions.icaoIssuingAuthorityChoices}
                  onChange={(val) => handleChange("issuing_authority", val)}
                  value={formData.issuing_authority}
                />
              </Grid>
              {formData.issuing_authority === "other" && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    id="other_issuing_authority"
                    fullWidth
                    variant="outlined"
                    value={formData.other_issuing_authority || ""}
                    onChange={(e) =>
                      handleChange("other_issuing_authority", e.target.value)
                    }
                    error={!!errors.other_issuing_authority}
                    helperText={errors.other_issuing_authority}
                    label={
                      <span>
                        Specify Issuing Authority <RequiredAsterisk />
                      </span>
                    }
                  />
                </Grid>
              )}
              {isExpiryDateVisible && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    id="certificate_expiry_date"
                    fullWidth
                    variant="outlined"
                    label={<span>Certificate Expiry Date</span>}
                    type="date"
                    value={formData.certificate_expiry_date}
                    onChange={(e) =>
                      handleChange("certificate_expiry_date", e.target.value)
                    }
                    error={!!errors.certificate_expiry_date}
                    helperText={errors.certificate_expiry_date}
                    slotProps={{
                      inputLabel: {
                        shrink: true,
                      },
                    }}
                  />
                </Grid>
              )}
            </>
          )}

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              id="assessment_date"
              fullWidth
              variant="outlined"
              label={<span>Assessment Date</span>}
              type="date"
              value={formData.assessment_date}
              onChange={(e) => handleChange("assessment_date", e.target.value)}
              error={!!errors.assessment_date}
              helperText={errors.assessment_date}
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              id="notes"
              fullWidth
              variant="outlined"
              value={formData.notes}
              onChange={(e) => handleChange("notes", e.target.value)}
              error={!!errors.notes}
              helperText={errors.notes}
              placeholder="Notes / limitations (e.g., Operational Level 4 – Valid until 2026)"
              multiline
              minRows={3}
              label="Notes"
            />
          </Grid>
        </Grid>
      </Box>

      <div className="flex justify-end space-x-2 mt-4">
        <Button onClick={onCancel}>Cancel</Button>
        <Button type="submit" variant="contained" color="primary">
          Save
        </Button>
      </div>
    </form>
  );
};

export default IcaoProficiencyForm;
